import { useEffect, useState } from 'react';
import type { Lot } from '../data/mockLots';
import { useCountdown } from '../hooks/useCountdown';
import { clickableProps } from '../lib/a11y';
import { revealProps } from '../lib/motion';
import { resolveFinish, resolveRarity } from '../lib/rarity';
import { BidderAvatar, RepeatBadge } from './BidderPill';
import { RingTimer } from './RingTimer';
import { SkinFrame } from './SkinFrame';
import { SkinThumbnail } from './SkinThumbnail';

const LIVE_PRICE = '#00ff94';
// The skin thumbnail sets the card's content height; the text columns on either
// side stretch to the same box and justify-between against it, so their top rows
// (title / timer) and bottom rows (stats / price) line up across the card.
const CONTENT_H = 84;

export function LiveLotCard({
  lot,
  index,
  onSelect,
}: {
  lot: Lot;
  index: number;
  onSelect?: () => void;
}) {
  const secondsLeft = useCountdown(lot.timerEndsAt);
  const [flash, setFlash] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: lot.bidsCount is the flash trigger, not read inside the effect
  useEffect(() => {
    setFlash((f) => f + 1);
  }, [lot.bidsCount]);

  const rarity = resolveRarity(lot);
  const finish = resolveFinish(lot);
  const leader = lot.lastBidder;
  const hasLeader = leader.firstName !== 'No bids yet';
  // The penny-auction hook: how far the live price still sits below market.
  // Cap at 99 so an early sub-cent price never reads as a misleading −100%.
  const discountPct =
    lot.currentPrice > 0 && lot.marketPrice > 0
      ? Math.min(99, Math.round((1 - lot.currentPrice / lot.marketPrice) * 100))
      : null;
  const marketLabel = `$${Math.round(lot.marketPrice).toLocaleString('en-US')}`;

  return (
    <article
      {...clickableProps(onSelect)}
      {...revealProps(index, {
        base: 120,
        className: `relative flex gap-3 p-2.5 pl-3.5 border border-edge bg-bg-elev/60 hover:border-edge-bright transition-colors ${onSelect ? 'cursor-pointer active:bg-bg-elev/80' : ''}`,
      })}
    >
      {/* rarity spine — quiet per-lot identity, mirrors RecentWinCard's accent bar */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[2px]"
        style={{ background: rarity.color, boxShadow: `0 0 8px ${rarity.color}99` }}
      />

      <div
        className="relative shrink-0 overflow-hidden"
        style={{ width: CONTENT_H, height: CONTENT_H }}
      >
        {lot.imageUrl ? (
          <SkinThumbnail
            imageUrl={lot.imageUrl}
            alt={`${lot.weaponLabel} | ${lot.skinName}`}
            weapon={lot.weapon}
            palette={lot.palette}
            rarityColor={lot.rarityColor ?? null}
            size="sm"
          />
        ) : (
          <SkinFrame weapon={lot.weapon} palette={lot.palette} size="sm" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between" style={{ height: CONTENT_H }}>
        <h3 className="font-display font-semibold text-[15px] leading-tight text-fg truncate">
          {finish && (
            <span style={{ color: finish.color }}>
              {finish.short}
              {finish.tm && <span className="text-[0.72em] align-top">™</span>}{' '}
            </span>
          )}
          {lot.weaponLabel}{' '}
          <span style={{ color: lot.palette.accent ?? lot.palette.to }}>| {lot.skinName}</span>
        </h3>

        <div className="flex items-center gap-1.5 font-mono text-[10px] leading-none tracking-[0.1em] min-w-0">
          <span
            aria-hidden
            className="inline-block w-[5px] h-[5px] rounded-full shrink-0"
            style={{ background: rarity.color, boxShadow: `0 0 6px ${rarity.color}` }}
          />
          <span className="shrink-0" style={{ color: rarity.color }}>
            {rarity.label}
          </span>
          <span className="text-fg-dim">·</span>
          <span className="text-fg/70 shrink-0">{lot.wear}</span>
        </div>

        <div className="flex items-center gap-1.5 min-w-0">
          {hasLeader ? (
            <BidderAvatar bidder={leader} size="sm" />
          ) : (
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-dashed border-fg-dim/50 bg-bg/40 font-mono text-[11px] text-fg-dim">
              —
            </div>
          )}
          <span className="font-medium text-[12px] leading-none text-fg truncate min-w-0">
            {hasLeader ? leader.firstName : 'No bids yet'}
          </span>
          {hasLeader && <RepeatBadge count={lot.lastBidderSeq ?? leader.bidCount} />}
        </div>

        <span className="font-mono text-[9px] leading-none tracking-wider text-fg-dim tabular-nums whitespace-nowrap">
          <span className="font-semibold text-fg-muted">{lot.bidsCount}</span> bids ·{' '}
          <span className="font-semibold text-fg-muted">{lot.uniqueBidders}</span> bidders
        </span>
      </div>

      <div
        className="flex shrink-0 flex-col items-end justify-between"
        style={{ height: CONTENT_H }}
      >
        <RingTimer
          secondsLeft={secondsLeft}
          timerEndsAt={lot.timerEndsAt}
          size={52}
          stroke={3.5}
          flashTrigger={flash}
          showLabel={false}
        />
        <div className="flex flex-col items-end gap-1 leading-none">
          <div
            className="font-mono font-bold text-[16px] tabular-nums"
            style={{ color: LIVE_PRICE }}
          >
            ${lot.currentPrice.toFixed(2)}
          </div>
          <div className="flex items-center gap-1.5 font-mono text-[9px] tabular-nums leading-none">
            <span className="text-fg-dim">{marketLabel}</span>
            {discountPct != null && (
              <span className="font-semibold text-primary/80">−{discountPct}%</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
